/**
 * OnlyOffice editor wrapper — toolbar + conflict-aware reload around OnlyOfficeModifier.
 *
 * Registered three times (cell/word/slide) via `defaultProps.documentType`. Only
 * active when VITE_OFFICE_PROVIDER=onlyoffice; Collabora's CollabExcelEditor etc.
 * stay the default.
 *
 * Conflict handling mirrors CollabExcelEditor via the engine-agnostic
 * `useWopiConflict` hook: on an external/agent edit (`papan:oo-conflict`), if the
 * editor is CLEAN it reloads in place; if it has the user's UNSAVED changes it
 * enters conflict mode (the "Your changes / Saved version" banner with Discard /
 * Keep-mine) instead of silently reloading over the edits. `OnlyOfficeModifier`
 * exposes exactly the refs the hook needs (refresh / save / discard / dirty).
 */
import { useRef, useState, useEffect, useCallback, lazy, Suspense, forwardRef, useImperativeHandle } from 'react'
import { Loader2 } from 'lucide-react'
import { request } from '@papan/api-client'
import { CollabStatusBadge } from './CollabStatusBadge.js'
import { EditorShareMenu } from './EditorShareMenu.js'
import { EditorToolbar } from './EditorToolbar.js'
import { ConflictBanner } from './ConflictBanner.js'
import { officeVersionsPath } from './office-versions.js'
import { useWopiConflict } from './useWopiConflict.js'
import { useOfficeViewMode } from './useOfficeViewMode.js'
import { OfficeCliRender } from './OfficeCliRender.js'
import type { ViewSyncRef } from './viewSync.js'
import type { EditorEditHandle, EditorUndoRedo } from '../client/editor-registry.js'
import { useUndoScopeGuard } from './useUndoScopeGuard.js'
import { useSavedVersionCover } from './useSavedVersionCover.js'

const LazyOnlyOfficeModifier = lazy(() =>
  import('./OnlyOfficeModifier.js').then(m => ({ default: m.OnlyOfficeModifier }))
)

const LoadingSpinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin themed-text-muted" size={24} />
  </div>
)

interface OnlyOfficeEditorProps {
  room: string
  fileId: string
  fileName?: string
  /** Injected via registration defaultProps. */
  documentType: 'cell' | 'word' | 'slide'
  isActive?: boolean
  /** readcopy / non-editable `.shared` pointers → open VIEW-only. */
  readOnly?: boolean
  /** VersionPreview pins a historical version → open read-only on that version. */
  versionId?: string
  /** Cross-view scroll/sheet state store, threaded from the workspace tab. */
  viewSync?: ViewSyncRef
  onDirtyChange?: (dirty: boolean) => void
  onConflictChange?: (conflicted: boolean) => void
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  /** Claim the app's undo scope so Ctrl+Z can't fall through to the
   *  drive's file-operation stack. See useUndoScopeGuard. */
  registerUndoRedo?: (editor: EditorUndoRedo) => () => void
  /** Opened from a SHARE LINK (the share view): the same OnlyOffice session, conflict and
   *  Discard behaviour as the drive (I33 — outsider = native), without the drive-only
   *  surfaces — no share menu, no officecli View (its route is not reachable by a share
   *  principal); a read-only link opens the read-only OnlyOffice editor on its own key. */
  shareView?: boolean
  /** In the share view: the visitor holds the version-history right (an Edit link or
   *  native access — `/info`'s `versionHistory`), so the conflict's Saved-version compare
   *  is offered. A View link sees the current content only. */
  canViewHistory?: boolean
}

export const OnlyOfficeEditor = forwardRef<EditorEditHandle, OnlyOfficeEditorProps>(
  function OnlyOfficeEditor(
    { room, fileId, fileName, documentType, isActive, readOnly, versionId, viewSync, onDirtyChange, onConflictChange, onSaveRef, onDiscardRef, registerUndoRedo, shareView, canViewHistory },
    ref,
  ) {
    const editorRef = useRef<EditorEditHandle>(null)
    const refreshRef = useRef<(() => Promise<boolean>) | null>(null)
    // Keep mine's confirmation, scoped to THIS editor (see useWopiConflict `sessionSavedRef`).
    const sessionSavedRef = useRef<(() => void) | null>(null)
    const fileType = (fileName?.split('.').pop() || '').toLowerCase()
    const isReadOnly = readOnly || !!versionId
    const [dirty, setDirty] = useState(false)

    useImperativeHandle(ref, () => ({
      copy: () => editorRef.current?.copy(),
      cut: () => editorRef.current?.cut(),
      paste: () => editorRef.current?.paste(),
      delete: () => editorRef.current?.delete(),
      getSelection: () => editorRef.current?.getSelection?.() ?? null,
      focusEditor: () => editorRef.current?.focusEditor?.(),
    }), [])

    // Called unconditionally (rules of hooks). In read-only mode the editor never
    // dirties, so `isConflicted` stays false and the conflict UI never renders.
    const conflict = useWopiConflict({
      fileId,
      onSaveRef: isReadOnly ? undefined : onSaveRef,
      onDiscardRef: isReadOnly ? undefined : onDiscardRef,
      refreshRef,
      sessionSavedRef,
      shareView,
      onDirtyChange: isReadOnly ? undefined : onDirtyChange,
      onConflictChange: isReadOnly ? undefined : onConflictChange,
      // OnlyOffice has no shared kit: a remote discard rotates the document.key, so
      // every OTHER tab must reload to leave the stale co-editing session. (Left on
      // even in read-only — a viewer should still see a discarded revert.)
      reloadOnRemoteDiscard: true,
    })

    // View/Edit toggle (#620, ported to OnlyOffice). View renders officecli's
    // read-only HTML (OfficeCliRender); Edit is the OnlyOffice session. The dirty
    // signal feeds the Edit→View save-gate (poll head contentHash before render).
    // When the document last picked up an edit we cannot see (its history
    // lives inside the DocsAPI iframe). Feeds the undo guard's recency
    // comparison; until it is set, an untouched tab claims nothing and the
    // explorer beside it keeps a working Ctrl+Z.
    const handleDirtyChange = useCallback((d: boolean) => { setDirty(d); conflict.handleDirtyChange(d) }, [conflict])
    const { viewMode, viewReady, showToggle, toggleNode } = useOfficeViewMode({
      readOnly: isReadOnly,
      fileId,
      fileName,
      isDirty: dirty,
      onSaveRef: isReadOnly ? undefined : onSaveRef,
      versionId, // a pinned version disables the officecli View (it renders the head)
      disabled: shareView,
    })
    const inView = viewMode === 'view' && !conflict.showSavedVersion

    // "Has this document been edited at all since it opened?" — NOT "is it
    // unsaved right now".
    //
    // The edits live inside the DocsAPI iframe where Papan cannot see them,
    // so Ctrl+Z on Papan's own chrome must not reach past them into the
    // drive. `dirty` looked like the condition and is the wrong one:
    // OnlyOffice autosaves every few seconds and clears it, while its undo
    // history keeps every one of those edits. In the window right after an
    // autosave the document is "clean" and full of undoable work, and a
    // Ctrl+Z aimed at the last sentence restored a file deleted minutes ago
    // instead.
    //
    // Registered whatever the View/Edit face shows — toggling to View does
    // not discard anything. A document nobody has typed in claims nothing, so
    // merely READING an office file leaves the explorer's undo as it was.
    const [everEdited, setEverEdited] = useState(false)
    useEffect(() => { if (dirty) setEverEdited(true) }, [dirty])
    useUndoScopeGuard(isReadOnly ? undefined : registerUndoRedo, everEdited)

    // Editor mount lifecycle. The OnlyOffice pane is VISIBLE (front) only outside
    // View and outside the saved-version preview. It mounts on the FIRST Edit, and
    // once it has LOADED (onReady → ooLoaded) it stays MOUNTED (hidden) across
    // View/Edit toggles, so re-entering Edit is instant and preserves cursor/scroll/
    // unsaved edits instead of re-opening the docservice room. BEFORE it loads it is
    // unmounted while hidden, so a toggle-to-View mid-load can't blank it (OnlyOffice
    // sizes its canvas at init). A pure view-only session (never enters Edit) never
    // mounts the editor. readOnly pins View → editorVisible is never true → no editor.
    const [hasEnteredEdit, setHasEnteredEdit] = useState(false)
    const [ooLoaded, setOoLoaded] = useState(false)
    // The Saved-version compare is version history: in the share view only for a visitor
    // holding that right (an Edit link or native access) — never hiding the editor for a
    // pane it can't show.
    const canCompare = !shareView || !!canViewHistory
    const showSavedVersion = canCompare && conflict.showSavedVersion
    const editorVisible = !inView && !showSavedVersion
    // #930 — the Saved version hides the live editor (display:none) while it stays mounted with its save hook: report
    // the cover, so Cmd+S / File → Save / this editor's commands are refused instead of writing the hidden document.
    useSavedVersionCover(!isReadOnly && showSavedVersion, () => conflict.setConflictView('mine'))
    useEffect(() => { if (editorVisible) setHasEnteredEdit(true) }, [editorVisible])
    const keepEditor = hasEnteredEdit && (editorVisible || ooLoaded)
    // If the editor unmounts (only happens hidden + pre-load), forget the loaded flag
    // so the next mount re-derives it from onReady.
    useEffect(() => { if (!keepEditor) setOoLoaded(false) }, [keepEditor])

    // The "Saved version" preview must load the COMMITTED content under a DISTINCT
    // document.key, or it collides with the main dirty editor's key (both resolve to
    // `fileId-{hash}-g{n}`). A read-only client joining the main editing session
    // corrupts it, so the next forcesave assembles nothing (error 4) and Overwrite
    // silently does nothing (#617). Previewing the LATEST version gives it the version
    // key `fileId-v-<id>` (distinct) AND the actual saved content. Fetched lazily when
    // the user opens the Saved view; reset per conflict so a new conflict re-fetches.
    // Anywhere the Saved-version pane is offered, an unreadable or empty list says so
    // instead of spinning forever (the share view offers none — `canCompare` above).
    const [savedVersionId, setSavedVersionId] = useState<string | null>(null)
    const [savedVersionFailed, setSavedVersionFailed] = useState(false)
    useEffect(() => { setSavedVersionId(null); setSavedVersionFailed(false) }, [conflict.conflictGeneration])
    useEffect(() => {
      if (isReadOnly || !showSavedVersion || savedVersionId) return
      let cancelled = false
      setSavedVersionFailed(false)
      request<any>(officeVersionsPath(fileId, shareView))
        .then((list) => {
          const arr = Array.isArray(list) ? list : (list?.versions || [])
          if (cancelled) return
          setSavedVersionId(arr[0]?.id ?? null)
          setSavedVersionFailed(!arr[0]?.id)
        })
        .catch(() => { if (!cancelled) setSavedVersionFailed(true) })
      return () => { cancelled = true }
    }, [isReadOnly, showSavedVersion, savedVersionId, fileId, shareView])

    // Latest committed version id for the View officecli-FAILURE fallback. Same #617
    // trap as the saved-version block: a read-only OnlyOffice session on the HEAD
    // (no versionId) resolves to the SAME document.key as the live edit session
    // (`fileId-{hash}-g{n}`) — local (the hidden kept-mounted editor) OR another
    // user's live edit — and joining it corrupts the next forcesave. So the fallback
    // must render the LATEST VERSION (key `fileId-v-<id>`, distinct + read-token, no
    // callbackUrl), which after the Edit→View save-gate IS the persisted head. Re-fetched
    // when viewReady flips (a dirty toggle just minted a new latest version). Null →
    // spinner, never a head-keyed session.
    const [latestVersionId, setLatestVersionId] = useState<string | null>(null)
    useEffect(() => {
      if (!inView) return
      let cancelled = false
      request<any>(officeVersionsPath(fileId, shareView))
        .then((list) => {
          const arr = Array.isArray(list) ? list : (list?.versions || [])
          if (!cancelled) setLatestVersionId(arr[0]?.id ?? null)
        })
        .catch(() => { /* leave null → spinner if officecli also fails */ })
      return () => { cancelled = true }
    }, [inView, viewReady, fileId, shareView])

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <EditorToolbar
          // Not while the Saved version covers the live editor: the toolbar sits above the hidden editor, and
          // switching to View force-saves it (`enterView`) — the write #930 refuses for Cmd+S, by mouse.
          mode={showToggle && !showSavedVersion ? toggleNode : undefined}
          status={<>{!shareView && <EditorShareMenu fileId={fileId} fileName={fileName} />}<CollabStatusBadge connected={true} /></>}
        />

        {!isReadOnly && conflict.isConflicted && (
          <ConflictBanner
            fileId={fileId}
            view={conflict.conflictView}
            onViewChange={conflict.setConflictView}
            onDiscard={conflict.handleDiscard}
            onOverwrite={conflict.handleOverwrite}
            canCompare={canCompare}
          />
        )}

        {/* Edit (OnlyOffice) — see the mount-lifecycle note above: mounts on the
            first Edit, then stays mounted (hidden) once loaded so re-Edit is instant
            and preserves state; unmounts if hidden before it finishes loading (avoids
            the display:none-mid-load blank). Hidden in View AND while previewing the
            saved version. */}
        {keepEditor && (
          <div className="flex-1 flex flex-col overflow-hidden" style={{ display: editorVisible ? undefined : 'none' }}>
            <Suspense fallback={LoadingSpinner}>
              <LazyOnlyOfficeModifier
                key={isReadOnly ? undefined : conflict.editorGeneration}
                ref={editorRef}
                fileId={fileId}
                fileName={fileName}
                fileType={fileType}
                documentType={documentType}
                room={room}
                mode={isReadOnly ? 'readonly' : 'edit'}
                versionId={versionId}
                isActive={isActive}
                onReady={() => setOoLoaded(true)}
                onDirtyChange={isReadOnly ? undefined : handleDirtyChange}
                onSaveRef={isReadOnly ? undefined : onSaveRef}
                onDiscardRef={isReadOnly ? undefined : onDiscardRef}
                onRefreshRef={refreshRef}
                onSessionSavedRef={sessionSavedRef}
                shareView={shareView}
              />
            </Suspense>
          </div>
        )}

        {/* View (officecli read-only). viewReady gates on the Edit→View save so a
            dirty toggle renders the persisted head, not stale bytes. On a render
            failure OfficeCliRender shows a read-only OnlyOffice session of the LATEST
            VERSION (distinct `-v-<id>` key — NOT the head, which would join and corrupt
            the live edit session, #617). No latest id yet → spinner, never a head key. */}
        {inView && (
          viewReady
            ? <OfficeCliRender
                fileId={fileId}
                fileName={fileName}
                viewSync={viewSync}
                mode="readonly"
                fallback={
                  latestVersionId ? (
                    <Suspense fallback={LoadingSpinner}>
                      <LazyOnlyOfficeModifier
                        key={`view-fallback-${latestVersionId}`}
                        fileId={fileId}
                        fileName={fileName}
                        fileType={fileType}
                        documentType={documentType}
                        mode="readonly"
                        versionId={latestVersionId}
                      />
                    </Suspense>
                  ) : LoadingSpinner
                }
              />
            : LoadingSpinner
        )}

        {/* Saved-version preview (read-only) while in conflict mode. Mounted as a
            version preview of the LATEST version so its document.key is `fileId-v-<id>`
            — distinct from the main editor's key, so it cannot join/corrupt the live
            editing session (which is what broke Overwrite). */}
        {!isReadOnly && showSavedVersion && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <Suspense fallback={LoadingSpinner}>
              {savedVersionId ? (
                <LazyOnlyOfficeModifier
                  key={`saved-${savedVersionId}`}
                  fileId={fileId}
                  fileName={fileName ? `${fileName} (Saved version)` : 'Saved version'}
                  fileType={fileType}
                  documentType={documentType}
                  mode="readonly"
                  versionId={savedVersionId}
                />
              ) : savedVersionFailed ? (
                <div className="flex-1 flex items-center justify-center text-xs themed-text-muted">
                  The saved version can't be shown here.
                </div>
              ) : LoadingSpinner}
            </Suspense>
          </div>
        )}
      </div>
    )
  },
)
